import { capitalize } from 'vue';

import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import EntityProperty from '@/common/models/EntityProperty';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { useMediaOwners, useMediaTypes } from '@/org-app/services/tanstack/queries';
import { runQueryInScope } from '@/org-app/services/tanstack/queryUtils';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

export interface IAudienceConcentrationPerUnitAPI {
  average_concentrations: number;
  daily_average: Record<string, unknown>;
  media_type: {
    code: string;
    enumeration_id: number;
    mediatype_id: number;
    name: string;
    parent_mediatype_id: number;
  };
  external_id: string;
  owner_id: number;
  location_name: string;
  site_id: number;
  site_name: string;
  unit_id: number;
}

export interface AudienceConcentrationPerUnitData {
  averageConcentrations: number;
  dailyAverage: Record<string, unknown>;
  mediaType: {
    code: string;
    enumerationID: number;
    mediatypeID: number;
    name: string;
    parentMediatypeID: number;
  };
  externalID: string;
  ownerID: number;
  cityName: string;
  siteID: number;
  siteName: string;
  unitID: number;
}

export class AudienceConcentrationPerUnit extends Validatable {
  data: AudienceConcentrationPerUnitData[];

  constructor() {
    super();
    this.data = [];
  }

  static {
    // When the user changes the language, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.languageChanged, () => {
      AudienceConcentrationPerUnit.entityPropertiesCached = null;
    });

    // When the user logs out or changes the organization, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.logout, () => {
      AudienceConcentrationPerUnit.entityPropertiesCached = null;
    });
    GlobalEventBus.$on(GlobalEvents.organizationChanged, () => {
      AudienceConcentrationPerUnit.entityPropertiesCached = null;
    });
  }

  addResponse(key: string, response: AudienceConcentrationPerUnitData): void {
    this.data.push(response);
  }

  static fromApi(apiData: Record<string, IAudienceConcentrationPerUnitAPI>): AudienceConcentrationPerUnit {
    const responseType = new AudienceConcentrationPerUnit();
    Object.keys(apiData).forEach((key) => {
      const entry = apiData[key];
      const mediaType = entry.media_type
        ? {
            code: entry.media_type.code,
            enumerationID: entry.media_type.enumeration_id,
            mediatypeID: entry.media_type.mediatype_id,
            name: entry.media_type.name,
            parentMediatypeID: entry.media_type.parent_mediatype_id,
          }
        : undefined;
      responseType.addResponse(key, {
        averageConcentrations: apiData[key].average_concentrations,
        dailyAverage: apiData[key].daily_average,
        mediaType,
        externalID: apiData[key].external_id,
        ownerID: apiData[key].owner_id,
        siteID: apiData[key].site_id,
        siteName: apiData[key].site_name,
        cityName: apiData[key].location_name,
        unitID: apiData[key].unit_id,
      });
    });
    return responseType;
  }

  static get entityProperties(): Record<string, EntityProperty<AudienceConcentrationPerUnit>> {
    if (AudienceConcentrationPerUnit.entityPropertiesCached != null) {
      return AudienceConcentrationPerUnit.entityPropertiesCached;
    }

    AudienceConcentrationPerUnit.entityPropertiesCached = {
      externalID: new EntityProperty({
        label: capitalize(i18n.global.t('names.screenID', { n: 1 })),
        property: 'externalID',
        type: TableColumnTypes.string,
      }),
      siteID: new EntityProperty({
        label: capitalize(i18n.global.t('names.siteID', { n: 1 })),
        property: 'siteID',
        type: TableColumnTypes.integer,
      }),
      siteName: new EntityProperty({
        label: capitalize(i18n.global.t('names.siteName', { n: 1 })),
        property: 'siteName',
        type: TableColumnTypes.string,
      }),
      cityName: new EntityProperty({
        label: capitalize(i18n.global.t('names.city', { n: 1 })),
        property: 'cityName',
        type: TableColumnTypes.string,
      }),
      averageConcentrations: new EntityProperty({
        label: capitalize(i18n.global.t('messages.averageAudienceConcentration')),
        property: 'averageConcentrations',
        type: TableColumnTypes.percentage,
      }),
      mediaTypeName: new EntityProperty({
        label: capitalize(i18n.global.t('names.venueType', { n: 1 })),
        property: 'media_type.id',
        type: TableColumnTypes.string,
        source: () => runQueryInScope(useMediaTypes),
        sourceFilterProperty: 'mediaTypeID',
        sourceGroupValues: 'children',
        sourceGroupLabel: 'formattedName',
      }),
      mediaOwner: new EntityProperty({
        label: capitalize(i18n.global.t('names.mediaOwner', { n: 1 })),
        property: 'owner_id',
        type: TableColumnTypes.list,
        source: () => runQueryInScope(useMediaOwners),
        sourceFilterProperty: 'mediaOwnerID',
      }),
    };

    return AudienceConcentrationPerUnit.entityPropertiesCached;
  }
}
